import {
  Chat,
  CheckCircle,
  ConfirmationNumber,
  Phone,
  Work,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ParticipantsAvatars from "../components/ParticipantsAvatars";
import ProfileRepliesChart from "../components/profile/ProfileRepliesChart";
import ReportsStatCard from "../components/reports/ReportsStatCard";
import { parsePaginatedResponse } from "../constants/pagination";
import { userProfilesAPI } from "../services/api";
import {
  getAvatarColor,
  getAvatarImageSrc,
  getAvatarInitials,
  hasProfilePicture,
} from "../utils/avatarUtils";
import { formatDate } from "../utils/dateFormatter";
import {
  getPriorityColor,
  getPriorityLabelKey,
  getTicketParticipants,
} from "../utils/ticketParticipants";

const TICKETS_PAGE_SIZE = 10;

const ProfilePage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [monthlyReplies, setMonthlyReplies] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  const [openTickets, setOpenTickets] = useState([]);
  const [openTotal, setOpenTotal] = useState(0);
  const [openPage, setOpenPage] = useState(1);

  const [closedTickets, setClosedTickets] = useState([]);
  const [closedTotal, setClosedTotal] = useState(0);
  const [closedPage, setClosedPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [openTicketsLoading, setOpenTicketsLoading] = useState(false);
  const [closedTicketsLoading, setClosedTicketsLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    const data = await userProfilesAPI.getMyProfile();
    setProfile(data);
  }, []);

  const loadDashboard = useCallback(async (year) => {
    const params = {};
    if (year != null) {
      params.year = year;
    }
    const data = await userProfilesAPI.getMyDashboard(params);
    setStats(data.stats);
    setMonthlyReplies(data.monthly_replies || []);
    if (data.selected_year != null) {
      setSelectedYear(data.selected_year);
    }
  }, []);

  const loadParticipatedTickets = useCallback(async (status, page) => {
    const data = await userProfilesAPI.getMyParticipatedTickets({
      status,
      page,
      page_size: TICKETS_PAGE_SIZE,
    });
    const { items, totalCount } = parsePaginatedResponse(data);
    if (status === "open") {
      setOpenTickets(items);
      setOpenTotal(totalCount);
    } else {
      setClosedTickets(items);
      setClosedTotal(totalCount);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        await loadProfile();
        await loadDashboard(null);
        await Promise.all([
          loadParticipatedTickets("open", 1),
          loadParticipatedTickets("closed", 1),
        ]);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("profile.loadError"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [loadProfile, loadDashboard, loadParticipatedTickets, t]);

  const handleYearChange = async (year) => {
    setSelectedYear(year);
    setChartLoading(true);
    try {
      await loadDashboard(year);
    } catch (err) {
      setError(err.message || t("profile.loadError"));
    } finally {
      setChartLoading(false);
    }
  };

  const handleOpenPageChange = async (_, page) => {
    setOpenPage(page);
    setOpenTicketsLoading(true);
    try {
      await loadParticipatedTickets("open", page);
    } catch (err) {
      setError(err.message || t("profile.loadError"));
    } finally {
      setOpenTicketsLoading(false);
    }
  };

  const handleClosedPageChange = async (_, page) => {
    setClosedPage(page);
    setClosedTicketsLoading(true);
    try {
      await loadParticipatedTickets("closed", page);
    } catch (err) {
      setError(err.message || t("profile.loadError"));
    } finally {
      setClosedTicketsLoading(false);
    }
  };

  const renderPagination = (page, total, onChange) => {
    const totalPages = Math.max(1, Math.ceil(total / TICKETS_PAGE_SIZE));
    if (total <= TICKETS_PAGE_SIZE) return null;
    const from = (page - 1) * TICKETS_PAGE_SIZE + 1;
    const to = Math.min(page * TICKETS_PAGE_SIZE, total);
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mt: 2,
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("referencePages.paginationShowing", { from, to, total })}
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={onChange}
          color="primary"
          showFirstButton
          showLastButton
          dir={isRTL ? "rtl" : "ltr"}
        />
      </Box>
    );
  };

  const displayName =
    profile?.arabic_name ||
    profile?.english_name ||
    profile?.user?.username ||
    t("common.user");

  const departmentNames = (profile?.departments || [])
    .map((d) => (isRTL ? d.name_ar : d.name_en) || d.name_ar)
    .filter(Boolean)
    .join(isRTL ? " ، " : ", ");

  const handleViewTicket = (ticketId) => {
    if (onNavigate && ticketId) {
      onNavigate(`/tickets/${ticketId}`);
    }
  };

  const renderTicketsTable = (tickets, emptyKey) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("profile.tables.ticketId")}</TableCell>
            <TableCell>{t("profile.tables.createdAt")}</TableCell>
            <TableCell>{t("profile.tables.subject")}</TableCell>
            <TableCell>{t("profile.tables.participants")}</TableCell>
            <TableCell>{t("profile.tables.priority")}</TableCell>
            <TableCell>{t("profile.tables.replies")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  {t(emptyKey)}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            tickets.map((ticket) => (
              <TableRow
                key={ticket.ticket_id}
                hover
                onClick={() => handleViewTicket(ticket.ticket_id)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>#{ticket.ticket_id}</TableCell>
                <TableCell>{formatDate(ticket.created_date)}</TableCell>
                <TableCell>{ticket.title || t("common.unknown")}</TableCell>
                <TableCell>
                  <ParticipantsAvatars
                    participants={getTicketParticipants(ticket)}
                    maxDisplay={3}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(getPriorityLabelKey(ticket.priority || "medium"))}
                    color={getPriorityColor(ticket.priority || "medium")}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{ticket.reply_count ?? 0}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container dir={isRTL ? "rtl" : "ltr"} maxWidth="xl" sx={{ mt: 1, mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", mb: 3 }}>
        {t("profile.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "center", sm: "flex-start" },
            gap: 3,
          }}
        >
          <Avatar
            src={getAvatarImageSrc({
              profile_picture: profile?.profile_picture,
              ...profile?.user,
            })}
            sx={{
              width: 96,
              height: 96,
              fontSize: "2rem",
              bgcolor: hasProfilePicture({ profile_picture: profile?.profile_picture })
                ? undefined
                : getAvatarColor(profile?.user),
            }}
          >
            {hasProfilePicture({ profile_picture: profile?.profile_picture })
              ? undefined
              : getAvatarInitials({
                  username: profile?.user?.username,
                  first_name: profile?.arabic_name,
                })}
          </Avatar>

          <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "start" } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              @{profile?.user?.username}
            </Typography>

            {profile?.job_title && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: { xs: "center", sm: "flex-start" },
                  mb: 1,
                }}
              >
                <Work fontSize="small" color="action" />
                <Typography variant="body2">{profile.job_title}</Typography>
              </Box>
            )}

            {departmentNames && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: { xs: "center", sm: "flex-start" },
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("profile.department")}:
                </Typography>
                <Typography variant="body2">{departmentNames}</Typography>
              </Box>
            )}

            {profile?.mobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: { xs: "center", sm: "flex-start" },
                }}
              >
                <Phone fontSize="small" color="action" />
                <Typography variant="body2" dir="ltr">
                  {profile.mobile}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportsStatCard
            title={t("profile.stats.replies")}
            value={stats?.reply_count}
            icon={<Chat />}
            color="primary.main"
            isRTL={isRTL}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportsStatCard
            title={t("profile.stats.ticketsOpened")}
            value={stats?.tickets_created}
            icon={<ConfirmationNumber />}
            color="info.main"
            isRTL={isRTL}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportsStatCard
            title={t("profile.stats.ticketsClosed")}
            value={stats?.tickets_closed}
            icon={<CheckCircle />}
            color="success.main"
            isRTL={isRTL}
          />
        </Grid>
      </Grid>

      <Box sx={{ mb: 3, position: "relative" }}>
        {chartLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.6)",
              zIndex: 1,
              borderRadius: 2,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        <ProfileRepliesChart
          data={monthlyReplies}
          isRTL={isRTL}
          selectedYear={selectedYear}
          onYearChange={handleYearChange}
        />
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t("profile.openTicketsTitle")}
        </Typography>
        {openTicketsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {renderTicketsTable(openTickets, "profile.noOpenTickets")}
            {renderPagination(openPage, openTotal, handleOpenPageChange)}
          </>
        )}
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t("profile.closedTicketsTitle")}
        </Typography>
        {closedTicketsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {renderTicketsTable(closedTickets, "profile.noClosedTickets")}
            {renderPagination(closedPage, closedTotal, handleClosedPageChange)}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ProfilePage;
